import { useEditorContext } from '@/hooks/useEditorContext';
import { useEditorProps } from '@/hooks/useEditorProps';
import { useLazyState } from '@/hooks/useLazyState';
import { HtmlStringToPreviewReactNodes } from '@/utils/HtmlStringToPreviewReactNodes';
import { stripMjmlDefaultHeadReset } from '@/utils/stripMjmlDefaultHeadReset';
import { JsonToMjml } from '@bay-email/core';
import { cloneDeep, isString } from 'lodash';
import mjml from 'mjml-browser';
import React, { useEffect, useMemo, useRef, useState } from 'react';

export const MOBILE_WIDTH = 320;

export const PreviewEmailContext = React.createContext<{
	html: string;
	reactNode: React.ReactNode | null;
	errMsg: React.ReactNode;
	mobileWidth: number;
}>({
	html: '',
	reactNode: null,
	errMsg: '',
	mobileWidth: 320,
});

export const PreviewEmailProvider: React.FC<{ children?: React.ReactNode }> = props => {
	const { current: iframe } = useRef(document.createElement('iframe'));
	const contentWindowRef = useRef<Window | null>(null);

	const [mobileWidth, setMobileWidth] = useState(MOBILE_WIDTH);

	const { pageData } = useEditorContext();
	const { onBeforePreview, mergeTags, previewInjectData, realUserEmailSignature } =
		useEditorProps();
	const [errMsg, setErrMsg] = useState<React.ReactNode>('');
	const [html, setHtml] = useState('');
	const lazyPageData = useLazyState(pageData, 0);

	const injectData = useMemo(() => {
		if (previewInjectData) {
			return previewInjectData;
		}
		if (mergeTags) return mergeTags;
		return {};
	}, [mergeTags, previewInjectData]);

	useEffect(() => {
		const breakpoint = parseInt(lazyPageData.data.value.breakpoint || '0');
		let adjustBreakPoint = breakpoint;
		if (breakpoint > 360) {
			adjustBreakPoint = Math.max(mobileWidth + 1, breakpoint);
		}
		const cloneData = {
			...lazyPageData,
			data: {
				...lazyPageData.data,
				value: {
					...lazyPageData.data.value,
					breakpoint: adjustBreakPoint + 'px',
				},
			},
		};

		// Mock signature HTML for preview
		const mockSignatureHtml = `
      <table cellpadding="0" cellspacing="0" border="0" class="signature-container" style="width: 100%; border-collapse: collapse;">
        <tr>
          <td class="signature-photo" style="width: 160px; vertical-align: top; padding: 20px;">
            <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=faces"
                  width="140" height="140"
                  style="display: block; width: 140px; height: 140px; border-radius: 4px;"
                  alt="John Doe" />
          </td>
          <td class="signature-info" style="vertical-align: top; padding: 20px;">
            <div style="font-family: Arial, sans-serif;">
              <div style="font-size: 20px; font-weight: bold; color: #1a1a1a; margin-bottom: 4px;">
                John Doe
              </div>
              <div style="font-size: 13px; color: #666666; margin-bottom: 12px;">
                LN3200584913
              </div>
              <div style="font-size: 14px; color: #333333; line-height: 1.6; margin-bottom: 12px;">
                <div style="margin-bottom: 4px;">
                  <span style="color: #888888;">Phone:</span> (555) 903-8506
                </div>
                <div style="margin-bottom: 4px;">
                  <a href="mailto:john.doe@email.com" style="color: #0066cc; text-decoration: none;">john.doe@email.com</a>
                </div>
                <div style="margin-bottom: 4px;">
                  <a href="https://backatyou.com" style="color: #0066cc; text-decoration: none;">backatyou.com</a>
                </div>
                <div style="color: #666666; font-size: 13px;">
                  700 Main Street Elm, NV 89101
                </div>
              </div>
							  </td>
          <td class="social-info" style="vertical-align: top; padding: 20px;">
              <div style="margin-top: 12px;">
                <img src="https://bay.cdn.bkat.io/cust/bay_demo/images/logos/cbaq01.png?v=64958"
											style="margin-right: 8px; vertical-align: middle; max-width: 100%; height: 32px;"
                      alt="Brokerage Logo" />
							</div>
							<div style="margin-top: 12px;">
								<a href="#" style="display: inline-block; margin-right: 8px;">
                  <img src="https://bay.cdn.bkat.io/smc/facebook-icon.png"
												width="20" height="20"
												style="display: block;"
												alt="Facebook" />
								</a>
								<a href="#" style="display: inline-block; margin-right: 8px;">
                  <img src="https://bay.cdn.bkat.io/smc/linkedin-icon.png"
												width="20" height="20"
												style="display: block;"
												alt="LinkedIn" />
								</a>
								<a href="#" style="display: inline-block; margin-right: 8px;">
                  <img src="https://bay.cdn.bkat.io/smc/twitter-logo-gray.png"
												width="20" height="20"
												style="display: block;"
                        alt="X" />
								</a>
								<a href="#" style="display: inline-block;">
                  <img src="https://bay.cdn.bkat.io/smc/instagram-icon.png"
												width="20" height="20"
												style="display: block;"
												alt="Instagram" />
								</a>
							</div>
						</div>
            </div>
          </td>
        </tr>
      </table>
    `;

		let parseHtml = mjml(
			JsonToMjml({
				data: cloneData,
				mode: 'production',
				context: cloneData,
				dataSource: {
					...cloneDeep(injectData),
					previewMode: true,
					AgentEmailSignature:
						realUserEmailSignature !== undefined ? realUserEmailSignature : mockSignatureHtml,
				},
				keepClassName: true,
			}),
		).html;
		parseHtml = stripMjmlDefaultHeadReset(parseHtml);
		if (onBeforePreview) {
			try {
				const result = onBeforePreview(parseHtml, injectData);
				if (isString(result)) {
					parseHtml = stripMjmlDefaultHeadReset(result);
					setHtml(parseHtml);
				} else {
					result.then(resHtml => {
						parseHtml = stripMjmlDefaultHeadReset(resHtml);
						setHtml(parseHtml);
					});
				}

				setErrMsg('');
			} catch (error: any) {
				setErrMsg(error?.message || error);
			}
		} else {
			setHtml(parseHtml);
		}

		return () => {
			setHtml('');
		};
	}, [injectData, onBeforePreview, lazyPageData, mobileWidth, realUserEmailSignature]);

	const htmlNode = useMemo(() => HtmlStringToPreviewReactNodes(html), [html]);

	useEffect(() => {
		if (errMsg) return;

		iframe.width = '400px';
		iframe.style.position = 'fixed';
		iframe.style.left = '-9999px';
		iframe.onload = evt => {
			contentWindowRef.current = (evt.target as any)?.contentWindow;
		};

		document.body.appendChild(iframe);

		return () => {
			document.body.removeChild(iframe);
		};
	}, [errMsg, html, iframe]);

	useEffect(() => {
		if (!contentWindowRef.current) return;
		const innerBody = contentWindowRef.current.document.body;
		innerBody.innerHTML = html;
		const a = innerBody.querySelector('.mjml-body') as HTMLElement;
		if (a) {
			a.style.display = 'inline-block';
			setMobileWidth(Math.max(a.clientWidth, MOBILE_WIDTH));
		}
		return () => {
			innerBody.innerHTML = '';
		};
	}, [html]);

	const value = useMemo(() => {
		return {
			reactNode: htmlNode,
			html,
			errMsg,
			mobileWidth,
		};
	}, [errMsg, html, htmlNode, mobileWidth]);

	return (
		<PreviewEmailContext.Provider value={value}>
			{props.children}
		</PreviewEmailContext.Provider>
	);
};
